import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, List, Tag } from 'lucide-react';
import { BUILD, BUILD_HISTORY } from '../buildInfo';
import { PartnerLogos } from './PartnerLogos';

const BRAND_LOGO_LIGHT = '/benchmark-ph-light.png';

export function GuideLayout({ title, subtitle, audience, sections, backTo, backLabel }) {
  const [tocOpen, setTocOpen] = useState(false);

  const allSections = [
    ...sections,
    {
      id: 'build-history',
      title: 'Build & Release History',
      body: (
        <div className="space-y-4">
          <p>
            Use this table to track which features were introduced in each build of Benchmark PH.
            The build number shown in the page header always reflects the version you are using.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-slate-200 rounded-xl overflow-hidden" data-testid="build-history-table">
              <thead>
                <tr className="bg-slate-50 text-left">
                  <th className="px-4 py-2 font-semibold text-slate-600 whitespace-nowrap">Build</th>
                  <th className="px-4 py-2 font-semibold text-slate-600 whitespace-nowrap">Published</th>
                  <th className="px-4 py-2 font-semibold text-slate-600">Changes</th>
                </tr>
              </thead>
              <tbody>
                {BUILD_HISTORY.map((rel) => (
                  <tr key={rel.build} className="border-t border-slate-100 align-top">
                    <td className="px-4 py-2 font-mono font-semibold text-primary whitespace-nowrap">{rel.build}</td>
                    <td className="px-4 py-2 text-slate-500 whitespace-nowrap">{rel.date}</td>
                    <td className="px-4 py-2">
                      <ul className="list-disc pl-4 space-y-1 text-slate-600">
                        {rel.changes.map((c, i) => (<li key={i}>{c}</li>))}
                      </ul>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ),
    },
  ];

  const scrollTo = (id) => {
    setTocOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="px-4 md:px-8 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-4 min-w-0">
            <img src={BRAND_LOGO_LIGHT} alt="Benchmark PH" className="h-10 object-contain flex-shrink-0" />
            <div className="min-w-0">
              <p className="font-semibold text-primary truncate flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-accent-dark flex-shrink-0" />
                {title}
              </p>
              <p className="text-xs text-slate-500 truncate">{audience}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <span
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 rounded-full text-xs font-mono font-semibold text-slate-600"
              data-testid="guide-build-badge"
            >
              <Tag className="w-3 h-3" />
              Build {BUILD.number} · {BUILD.published}
            </span>
            <Link
              to={backTo}
              className="text-slate-500 hover:text-primary flex items-center gap-2 text-sm"
              data-testid="guide-back-link"
            >
              <ArrowLeft className="w-4 h-4" />
              {backLabel}
            </Link>
          </div>
        </div>
      </header>

      <main className="px-4 md:px-8 py-8 max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-primary mb-2">{title}</h1>
          <p className="text-slate-500 max-w-2xl">{subtitle}</p>
          <p className="sm:hidden mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 rounded-full text-xs font-mono font-semibold text-slate-600">
            <Tag className="w-3 h-3" /> Build {BUILD.number} · {BUILD.published}
          </p>
        </div>

        <div className="lg:grid lg:grid-cols-[260px_1fr] lg:gap-8">
          {/* TOC */}
          <aside className="mb-6 lg:mb-0">
            <div className="lg:sticky lg:top-24 bg-white rounded-2xl border border-slate-200 p-4" data-testid="guide-toc">
              <button
                className="flex items-center gap-2 font-semibold text-primary text-sm w-full lg:cursor-default"
                onClick={() => setTocOpen(!tocOpen)}
                data-testid="toc-toggle"
              >
                <List className="w-4 h-4 text-accent-dark" />
                Table of Contents
              </button>
              <ol className={`mt-3 space-y-1 ${tocOpen ? 'block' : 'hidden'} lg:block`}>
                {allSections.map((s, i) => (
                  <li key={s.id}>
                    <button
                      onClick={() => scrollTo(s.id)}
                      className="w-full text-left text-sm text-slate-600 hover:text-accent-dark hover:bg-accent/5 rounded-lg px-2 py-1.5 transition-colors"
                      data-testid={`toc-${s.id}`}
                    >
                      <span className="font-mono text-xs text-slate-400 mr-2">{i + 1}.</span>
                      {s.title}
                    </button>
                  </li>
                ))}
              </ol>
            </div>
          </aside>

          {/* Content */}
          <div className="space-y-6">
            {allSections.map((s, i) => (
              <section
                key={s.id}
                id={s.id}
                className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 scroll-mt-24"
                data-testid={`guide-section-${s.id}`}
              >
                <h2 className="text-lg font-bold text-primary mb-4 flex items-baseline gap-2">
                  <span className="font-mono text-sm text-accent-dark">{i + 1}.</span>
                  {s.title}
                </h2>
                <div className="text-sm text-slate-600 leading-relaxed space-y-3 guide-content">
                  {s.body}
                </div>
              </section>
            ))}
          </div>
        </div>
      </main>

      <footer className="px-4 py-6 text-center text-slate-500 text-sm border-t border-slate-200 bg-white mt-8">
        <PartnerLogos className="mb-3" />
        Developed for <span className="text-slate-600">IBPAP</span> by{' '}
        <a href="https://sustek.ai/solutions/soctech" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 hover:underline">
          SusTek SocTech Labs
        </a>
      </footer>
    </div>
  );
}

export function GuideList({ items }) {
  return (
    <ul className="list-disc pl-5 space-y-1.5">
      {items.map((item, i) => (<li key={i}>{item}</li>))}
    </ul>
  );
}

export function GuideSteps({ steps }) {
  return (
    <ol className="list-decimal pl-5 space-y-1.5">
      {steps.map((s, i) => (<li key={i}>{s}</li>))}
    </ol>
  );
}

export function GuideTip({ children }) {
  return (
    <div className="border-l-4 border-accent bg-accent/5 rounded-r-xl px-4 py-3 text-slate-700">
      <span className="font-semibold text-accent-dark">Tip: </span>{children}
    </div>
  );
}
